/**
 * SEO component — page-level SEO data setter.
 *
 * Does NOT render a <Helmet> itself.
 * Pushes data into SEOContext via useLayoutEffect (synchronous before paint).
 * No cleanup on unmount — avoids the "blank title" window between route changes.
 *
 * enabled?: boolean — when false, suppresses the setSEO call entirely.
 * Used by MobileTabNavigation to prevent non-active pages from overwriting
 * the active page's head tags. All pages render in the same wrapper type,
 * so the swipe track never remounts.
 */

import React, { useLayoutEffect } from 'react';
import { useSEOContext } from '@/hooks/use-seo';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object | object[];
  enabled?: boolean;
}

export const SEO: React.FC<SEOProps> = (props) => {
  const { setSEO } = useSEOContext();
  const isEnabled = props.enabled !== false;
  const schemaKey = props.schema ? JSON.stringify(props.schema) : '';

  useLayoutEffect(() => {
    if (!isEnabled) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { enabled, ...seoData } = props;
    setSEO(seoData);
  }, [
    isEnabled,
    props.title,
    props.description,
    props.canonical,
    props.ogType,
    props.ogImage,
    schemaKey,
    setSEO,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// Re-export for convenience
export type { SEOProps };
